<template>
    <div class="w-full min-h-full flex flex-col justify-center">
        <FormKit
            v-slot="{ state: { valid }, value }"
            type="form"
            :actions="false"
            :incomplete-message="false"
            form-class="w-full mx-auto lg:grid lg:grid-cols-[auto_1px_auto] lg:gap-x-[139px] lg:pt-[62px]"
            @submit="onSubmit"
        >
            <ShcUiHeading
                class="mb-8 font-semibold lg:text-[28px] lg:w-[378px] lg:justify-self-end lg:mb-12"
                :tag="HEADING_TAG.h1"
                data-cy="login_heading"
            >
                {{ $t('Bbk.form.register.title') }}
            </ShcUiHeading>
            <div
                class="min-h-full justify-center md:justify-start flex flex-col w-full lg:row-start-2 lg:w-[378px] lg:justify-self-end"
            >
                <BaseInput
                    type="text"
                    name="firstName"
                    :label="$t('Bbk.form.global.firstName')"
                    :autocomplete="INPUT_AUTOCOMPLETE['given-name']"
                    locator-prefix="register_firstname"
                    validation="required"
                    has-info-icon
                    :info-text="$t('Bbk.form.register.infoText')"
                />
                <BaseInput
                    type="text"
                    name="lastName"
                    :label="$t('Bbk.form.global.lastName')"
                    :autocomplete="INPUT_AUTOCOMPLETE['family-name']"
                    locator-prefix="register_lastname"
                    validation="required"
                    has-info-icon
                    :info-text="$t('Bbk.form.register.infoText')"
                />
                <BaseInput
                    type="tel"
                    name="phoneNumber"
                    :label="`${$t('Bbk.form.global.phone')}${value['agreement-sms'] ? '*' : ''}`"
                    :autocomplete="INPUT_AUTOCOMPLETE.tel"
                    locator-prefix="phone_number"
                    :validation="[['matches', '/^\\d{9,13}$/'], [value['agreement-sms'] && 'required']]"
                    has-info-icon
                    :info-text="$t('Bbk.form.register.infoText')"
                />
                <BaseInput
                    name="email"
                    :label="$t('Bbk.form.register.email')"
                    type="email"
                    :autocomplete="INPUT_AUTOCOMPLETE.email"
                    locator-prefix="register_email"
                    validation="required|email"
                    has-info-icon
                    :info-text="$t('Bbk.form.register.infoText')"
                />
                <hr class="w-[76px] border-none h-px bg-black mx-auto my-4" />
                <FormKit v-slot="{ value: { password } }" name="passwordGroup" type="group">
                    <BaseInput
                        name="password"
                        :label="$t('Bbk.form.register.password')"
                        :type="passwordInputType"
                        :autocomplete="INPUT_AUTOCOMPLETE['current-password']"
                        locator-prefix="register_password"
                        :validation="passwordValidation"
                        :suffix-icon="passwordSuffixIcon"
                        has-info-icon
                        :info-text="$t('Bbk.form.register.infoText')"
                        @suffix-icon-click="handlePasswordSuffixIconEvent"
                    />
                    <PasswordStrength class="-mt-4 mb-4" :password="password" />
                    <BaseInput
                        name="password_confirm"
                        :label="$t('Bbk.form.register.repeatPassword')"
                        :type="passwordRepeatInputType"
                        :autocomplete="INPUT_AUTOCOMPLETE['current-password']"
                        locator-prefix="register_password_2"
                        validation="required|confirm"
                        :suffix-icon="passwordRepeatSuffixIcon"
                        has-info-icon
                        :info-text="$t('Bbk.form.register.infoText')"
                        @suffix-icon-click="handlePasswordRepeatSuffixIconEvent"
                    />
                </FormKit>
            </div>
            <hr class="hidden lg:block h-full w-px border-[0.5px] border-black lg:row-start-2" />
            <div class="mt-4 lg:mt-0 lg:row-start-2 lg:w-[378px]">
                <BaseCheckbox
                    v-for="agreement in agreements"
                    :key="agreement?.code"
                    outer-class="mb-4"
                    type="checkbox"
                    v-bind="{
                        name: `agreement-${agreement?.code}`,
                        isHtml: agreement?.isHtml,
                        ...(agreement?.isRequired && {
                            validation: 'required|accepted',
                        }),
                    }"
                >
                    <template #label>
                        <ExpandHtml v-if="agreement?.isHtml" :max="4" :html="agreement?.content" />
                        <template v-else>{{ agreement?.content }}</template>
                    </template>
                </BaseCheckbox>
                <ShcUiParagraph class="!text-2xs !leading-4">
                    {{ $t('Bbk.form.dataAdministrator') }}
                    <ShcUiLink target="_blank" :theme="LINK_THEME.inline" :to="`${siteUrl}/${$i18n.locale.value}/rodo`">
                        {{ ` ${$t('Bbk.global.here')}` }}
                    </ShcUiLink>
                </ShcUiParagraph>
                <ShcUiButton
                    type="submit"
                    data-cy="register_create_account_btn"
                    class="uppercase mt-8 disabled:bg-bbk-grey"
                    full
                    :disabled="!valid"
                >
                    <LazyShcUiLoader
                        v-if="isRegisterInProgress"
                        size="tiny"
                        class="w-[1.125rem] h-[1.125rem] lg:w-5 lg:h-5"
                    />
                    <span v-else>{{ $t('Bbk.form.register.createAccount') }}</span>
                </ShcUiButton>
            </div>
        </FormKit>
    </div>
</template>

<script lang="ts" setup>
    import { HEADING_TAG } from '@shc/nuxt-shc-ui-layer/components/Heading/heading'
    import type { Props } from './props'
    import { getPasswordValidationRules } from '@/utils/getPasswordValidationRules'
    import { LINK_THEME } from '@/components/Shc/Ui/Link/link'
    import { INPUT_AUTOCOMPLETE, INPUT_TYPE } from '@/components/Shc/Ui/Input/input'
    import { handlePasswordSuffixIcon } from '@/utils/handlePasswordSuffixIcon'
    import type { FormData, Register } from '@/types/registration'

    const props = defineProps<Props>()
    const emit = defineEmits<{
        customerRegister: [formData: Register]
    }>()
    const { siteUrl } = useRuntimeConfig().public
    const { $i18n, $bus } = useNuxtApp()
    const passwordInputType = ref(INPUT_TYPE.password)
    const passwordSuffixIcon = ref('visibility_off')
    const passwordRepeatInputType = ref(INPUT_TYPE.password)
    const passwordRepeatSuffixIcon = ref('visibility_off')
    const passwordValidation =
        props.numberOfRequiredCharacterClasses && props.minimumPasswordLength
            ? getPasswordValidationRules(props.numberOfRequiredCharacterClasses, props.minimumPasswordLength)
            : undefined
    const isRegisterInProgress = ref(false)

    function handlePasswordSuffixIconEvent() {
        handlePasswordSuffixIcon({
            passwordInputType,
            passwordSuffixIcon,
        })
    }

    function handlePasswordRepeatSuffixIconEvent() {
        handlePasswordSuffixIcon({
            passwordInputType: passwordRepeatInputType,
            passwordSuffixIcon: passwordRepeatSuffixIcon,
        })
    }

    function onSubmit(formData: FormData) {
        isRegisterInProgress.value = true

        const newsletterAgreements = [
            ...(formData['agreement-email'] ? ['email'] : []),
            ...(formData['agreement-sms'] ? ['sms'] : []),
        ]

        emit('customerRegister', {
            email: formData.email,
            ...(formData['agreement-1'] && {
                registrationAgreements: ['1'],
            }),
            newsletterAgreements,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phoneNumber: formData.phoneNumber,
            password: formData.passwordGroup.password,
        })
    }

    $bus.$on('customerCreateDone', () => {
        isRegisterInProgress.value = false
    })
</script>
